import { useEffect, useMemo, useState } from 'react';
import {
  Box, Typography, Button, Card, CardContent, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, CircularProgress, Alert,
  Drawer, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Stack, Popover, Checkbox, List, ListItemButton, ListItemIcon, ListItemText,
  InputAdornment, Divider, Badge,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import LockIcon from '@mui/icons-material/Lock';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useQueryClient } from '@tanstack/react-query';
import type { DatalonPayLine } from '../../types';
import { api } from '../../services/api';
import DateRangePicker, { getDefaultDateRange, formatDateRange } from '../../components/common/DateRangePicker';
import {
  usePayrollBreakdownAll,
  usePayrollBreakdown,
  useCreatePayrollManualEntry,
  useLockPayrollPeriod,
  usePayrollSnapshots,
} from '../../hooks/useApi';
import type { DateRange, PayrollBreakdown, PayrollManualEntryType } from '../../types';

const fmt = (n: number) =>
  new Intl.NumberFormat('da-DK', { style: 'currency', currency: 'DKK', minimumFractionDigits: 0 }).format(n);

const fmtHours = (n: number) =>
  n === 0 ? '—' : new Intl.NumberFormat('da-DK', { maximumFractionDigits: 1 }).format(n);

const dayTypeLabel: Record<string, { label: string; color: any }> = {
  WEEKDAY: { label: 'Hverdag', color: 'default' },
  SATURDAY: { label: 'Lørdag', color: 'warning' },
  SUNDAY: { label: 'Søndag', color: 'error' },
  HOLIDAY_FULL: { label: 'Helligdag', color: 'error' },
  HOLIDAY_HALF: { label: 'Halv helligdag', color: 'warning' },
};

const manualTypeLabels: Record<PayrollManualEntryType, string> = {
  SYGDOM: 'Sygdom',
  FERIE: 'Ferie',
  FERIEFRIDAG: 'Feriefridag',
  KURSUS: 'Kursus',
  BARN_SYG: 'Barn syg',
  HELLIGDAG_BETALT: 'Betalt helligdag',
  ANDET: 'Andet',
};

export default function PayrollOverview() {
  const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
  const { from, to } = formatDateRange(dateRange);
  const { data: breakdowns = [], isLoading, error } = usePayrollBreakdownAll(from, to);
  const { data: snapshots = [] } = usePayrollSnapshots(from, to);

  const [selectedDriverId, setSelectedDriverId] = useState<string | null>(null);
  const [manualEntryOpen, setManualEntryOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [sendApprovalOpen, setSendApprovalOpen] = useState(false);
  // Multi-select driver filter (tomt array = vis alle)
  const [filterDriverIds, setFilterDriverIds] = useState<string[]>([]);

  // Filtreret liste til tabel + KPI (og CSV-eksport)
  const filteredBreakdowns = useMemo(() => {
    if (filterDriverIds.length === 0) return breakdowns;
    const set = new Set(filterDriverIds);
    return breakdowns.filter((b: PayrollBreakdown) => set.has(b.driverId));
  }, [breakdowns, filterDriverIds]);

  const selectedBreakdown = selectedDriverId
    ? breakdowns.find((b: PayrollBreakdown) => b.driverId === selectedDriverId)
    : null;

  const totals = filteredBreakdowns.reduce(
    (acc: any, b: PayrollBreakdown) => ({
      normal: acc.normal + b.totals.normalHours,
      ot1: acc.ot1 + b.totals.ot1Hours,
      ot2: acc.ot2 + b.totals.ot2Hours,
      eve: acc.eve + b.totals.eveAllowanceHours,
      night: acc.night + b.totals.nightAllowanceHours,
      gross: acc.gross + b.totals.grossBeforePension,
      employerCost: acc.employerCost + b.totals.totalEmployerCost,
    }),
    { normal: 0, ot1: 0, ot2: 0, eve: 0, night: 0, gross: 0, employerCost: 0 },
  );


  const snapshotsByDriver = new Map<string, any>();
  for (const s of snapshots) {
    snapshotsByDriver.set(s.driverId, s);
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6">Lønoverblik</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <DriverFilterButton
            breakdowns={breakdowns}
            filterDriverIds={filterDriverIds}
            onChange={setFilterDriverIds}
          />
          <DateRangePicker value={dateRange} onChange={setDateRange} />
          <Button
            variant="outlined"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => setExportDialogOpen(true)}
            disabled={breakdowns.length === 0}
          >
            Eksport CSV
          </Button>
          <Button
            variant="contained"
            size="small"
            onClick={() => setSendApprovalOpen(true)}
            disabled={breakdowns.length === 0}
          >
            Send til godkendelse
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Kunne ikke hente lønoverblik. Kontrollér at regelsæt er oprettet.
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : breakdowns.length === 0 ? (
        <Alert severity="info">
          Ingen løndata for perioden. Sørg for at der er synkroniseret arbejdstimer, og at chaufførerne har en profil.
        </Alert>
      ) : (
        <>
          {/* KPI strip */}
          <Card sx={{ mb: 2 }}>
            <CardContent sx={{ py: 1.5 }}>
              <Box sx={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                <Kpi label="Normaltimer" value={`${fmtHours(totals.normal)} t`} />
                <Kpi label="50% overtimer" value={`${fmtHours(totals.ot1)} t`} color="warning.main" />
                <Kpi label="100% overtimer" value={`${fmtHours(totals.ot2)} t`} color="error.main" />
                <Kpi label="Nat-/aften-tillæg" value={`${fmtHours(totals.eve + totals.night)} t`} />
                <Kpi label="Brutto" value={fmt(totals.gross)} color="primary.main" />
                <Kpi label="Total AG-omk." value={fmt(totals.employerCost)} color="text.primary" />
              </Box>
            </CardContent>
          </Card>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Chauffør</TableCell>
                  <TableCell>Ansættelse</TableCell>
                  <TableCell align="right">Normal</TableCell>
                  <TableCell align="right">50%</TableCell>
                  <TableCell align="right">100%</TableCell>
                  <TableCell align="right">Aften</TableCell>
                  <TableCell align="right">Nat</TableCell>
                  <TableCell align="right">Brutto</TableCell>
                  <TableCell align="right">AG total</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredBreakdowns.map((b: PayrollBreakdown) => {
                  const snapshot = snapshotsByDriver.get(b.driverId);
                  return (
                    <TableRow
                      key={b.driverId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => setSelectedDriverId(b.driverId)}
                    >
                      <TableCell>{b.driverName}</TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {b.employmentType === 'TIMELOENNET' ? 'Timeløn' : 'Fuldløn'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{fmtHours(b.totals.normalHours)}</TableCell>
                      <TableCell align="right">{fmtHours(b.totals.ot1Hours)}</TableCell>
                      <TableCell align="right">{fmtHours(b.totals.ot2Hours)}</TableCell>
                      <TableCell align="right">{fmtHours(b.totals.eveAllowanceHours)}</TableCell>
                      <TableCell align="right">{fmtHours(b.totals.nightAllowanceHours)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>
                        {fmt(b.totals.grossBeforePension)}
                      </TableCell>
                      <TableCell align="right">{fmt(b.totals.totalEmployerCost)}</TableCell>
                      <TableCell align="center">
                        {snapshot ? (
                          <Chip size="small" color="success" icon={<LockIcon sx={{ fontSize: 12 }} />} label="Låst" />
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            —
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {/* CSV-eksport dialog */}
      {exportDialogOpen && (
        <ExportPayrollCsvDialog
          from={from} to={to}
          selectedDrivers={filteredBreakdowns.map((b: PayrollBreakdown) => ({ driverId: b.driverId, driverName: b.driverName }))}
          totalDrivers={breakdowns.length}
          onClose={() => setExportDialogOpen(false)}
        />
      )}

      {sendApprovalOpen && (
        <SendForApprovalDialog
          from={from} to={to}
          availableDrivers={filteredBreakdowns.map((b: PayrollBreakdown) => ({ driverId: b.driverId, driverName: b.driverName }))}
          onClose={() => setSendApprovalOpen(false)}
        />
      )}

      {/* Driver drawer */}
      <Drawer
        anchor="right"
        open={!!selectedDriverId}
        onClose={() => setSelectedDriverId(null)}
        PaperProps={{ sx: { width: { xs: '100%', md: 720 } } }}
      >
        {selectedBreakdown && (
          <PayrollDriverDetail
            breakdown={selectedBreakdown}
            periodStart={from}
            periodEnd={to}
            isLocked={!!snapshotsByDriver.get(selectedBreakdown.driverId)}
            onClose={() => setSelectedDriverId(null)}
            onOpenManualEntry={() => setManualEntryOpen(true)}
          />
        )}
      </Drawer>

      {/* Manual entry dialog */}
      {manualEntryOpen && selectedBreakdown && (
        <ManualEntryDialog
          driverId={selectedBreakdown.driverId}
          driverName={selectedBreakdown.driverName}
          periodStart={from}
          periodEnd={to}
          onClose={() => setManualEntryOpen(false)}
        />
      )}
    </Box>
  );
}

function Kpi({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1 }}>
        {label}
      </Typography>
      <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.3, color: color || 'text.primary' }}>
        {value}
      </Typography>
    </Box>
  );
}

function PayrollDriverDetail({
  breakdown, periodStart, periodEnd, isLocked, onClose, onOpenManualEntry,
}: {
  breakdown: PayrollBreakdown;
  periodStart: string;
  periodEnd: string;
  isLocked: boolean;
  onClose: () => void;
  onOpenManualEntry: () => void;
}) {
  const { data: live } = usePayrollBreakdown(breakdown.driverId, periodStart, periodEnd);
  const b = live || breakdown;
  const lockMutation = useLockPayrollPeriod();
  const [editDay, setEditDay] = useState<{ date: string } | null>(null);

  async function handleLock() {
    if (!confirm(`Lås periode ${periodStart} – ${periodEnd} for ${b.driverName}? Låste perioder kan ikke ændres medmindre de oplåses.`)) return;
    await lockMutation.mutateAsync({
      driverId: b.driverId,
      periodStart,
      periodEnd,
      periodType: 'WEEK',
    });
  }

  return (
    <Box>
      {/* Drawer header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Typography variant="h6">{b.driverName}</Typography>
          <Typography variant="caption" color="text.secondary">
            {periodStart} – {periodEnd} · {b.employmentType === 'TIMELOENNET' ? 'Timeløn' : 'Fuldløn'}
          </Typography>
        </Box>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        {b.warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {b.warnings.join('. ')}
          </Alert>
        )}

        {/* Totals */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Sådan bliver der betalt
            </Typography>
            {b.payable && b.payable.lines.length > 0 ? (
              <Table size="small" sx={{ mb: 2 }}>
                <TableBody>
                  {b.payable.lines.map((l: DatalonPayLine) => (
                    <TableRow key={l.lonartNumber + l.name}>
                      <TableCell sx={{ pl: 0 }}>
                        {l.name}
                        <Typography component="span" variant="caption" color="text.secondary"> · lønart {l.lonartNumber}</Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>{fmtHours(l.antal)}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>
                        {l.rate > 0 ? `${fmt(l.rate)}/t` : '—'}
                      </TableCell>
                      <TableCell align="right">{l.rate > 0 ? fmt(l.belob) : ''}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ borderTop: 2, pl: 0 }} colSpan={3}><strong>I alt til udbetaling</strong></TableCell>
                    <TableCell sx={{ borderTop: 2 }} align="right"><strong>{fmt(b.payable.total)}</strong></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Ingen Dataløn-satser opsat for denne chauffør — opsæt lønarter og
                satser under Indstillinger → Dataløn.
              </Typography>
            )}

            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Overenskomstens standardsatser (til sammenligning)
            </Typography>
            <Table size="small">
              <TableBody>
                <TotalRow label="Grundløn" hours={b.totals.normalHours} amount={b.totals.baseAmount} />
                <TotalRow label="50% overtid" hours={b.totals.ot1Hours} amount={b.totals.ot1Amount} />
                <TotalRow label="100% overtid" hours={b.totals.ot2Hours} amount={b.totals.ot2Amount} />
                <TotalRow label="Aften-tillæg" hours={b.totals.eveAllowanceHours} amount={b.totals.allowanceAmount} suffix=" (aften + nat)" />
                <TotalRow label="Nat-tillæg" hours={b.totals.nightAllowanceHours} amount={0} hideAmount />
                <TableRow>
                  <TableCell sx={{ borderTop: 2 }} colSpan={2}><strong>Brutto</strong></TableCell>
                  <TableCell sx={{ borderTop: 2 }} align="right"><strong>{fmt(b.totals.grossBeforePension)}</strong></TableCell>
                </TableRow>
                <TotalRow label="Frihedsopsparing 6,75%" hours={null} amount={b.totals.holidayAccrual} />
                <TotalRow label="Særligt løntillæg" hours={null} amount={b.totals.specialSavings} />
                <TotalRow label="Pension AG" hours={null} amount={b.totals.pensionEmployer} />
                <TableRow>
                  <TableCell sx={{ borderTop: 2 }} colSpan={2}><strong>Total arbejdsgiver-omk.</strong></TableCell>
                  <TableCell sx={{ borderTop: 2 }} align="right"><strong>{fmt(b.totals.totalEmployerCost)}</strong></TableCell>
                </TableRow>
                <TotalRow label="Pension LM (trækkes)" hours={null} amount={b.totals.pensionEmployee} />
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Per-day */}
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Dag for dag</Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dato</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Normal</TableCell>
                    <TableCell align="right">50%</TableCell>
                    <TableCell align="right">100%</TableCell>
                    <TableCell align="right">Aften</TableCell>
                    <TableCell align="right">Nat</TableCell>
                    <TableCell align="right">Dag total</TableCell>
                    <TableCell align="right" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {b.days.map((d: any) => {
                    const dayGross = d.baseAmount + d.ot1Amount + d.ot2Amount + d.eveAmount + d.nightAmount;
                    const cfg = dayTypeLabel[d.dayType] || dayTypeLabel.WEEKDAY;
                    return (
                      <TableRow key={d.date}>
                        <TableCell>{d.date}</TableCell>
                        <TableCell>
                          <Chip size="small" label={cfg.label} color={cfg.color} variant="outlined" />
                          {d.manualEntries.length > 0 && d.manualEntries.map((m: any, i: number) => (
                            <Chip key={i} size="small" label={m.type} sx={{ ml: 0.5 }} color="info" variant="outlined" />
                          ))}
                        </TableCell>
                        <TableCell align="right">{fmtHours(d.normalMin / 60)}</TableCell>
                        <TableCell align="right">{fmtHours(d.ot1Min / 60)}</TableCell>
                        <TableCell align="right">{fmtHours(d.ot2Min / 60)}</TableCell>
                        <TableCell align="right">{fmtHours(d.eveAllowanceMin / 60)}</TableCell>
                        <TableCell align="right">{fmtHours(d.nightAllowanceMin / 60)}</TableCell>
                        <TableCell align="right">{fmt(dayGross)}</TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => setEditDay({ date: d.date })}
                            disabled={isLocked}
                            title="Rediger registreringer for denne dag"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>

        {/* Actions */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button startIcon={<AddIcon />} onClick={onOpenManualEntry} variant="outlined" size="small">
            Tilføj fravær (sygdom/ferie/kursus)
          </Button>
          {!isLocked && (
            <Button
              startIcon={<LockIcon />}
              variant="contained"
              size="small"
              onClick={handleLock}
              disabled={lockMutation.isPending}
            >
              Lås periode
            </Button>
          )}
          {isLocked && (
            <Chip icon={<LockIcon />} label="Periode låst" color="success" />
          )}
        </Box>
      </Box>

      {editDay && (
        <EditDayDialog
          driverId={b.driverId}
          driverName={b.driverName}
          date={editDay.date}
          onClose={() => setEditDay(null)}
          periodStart={periodStart}
          periodEnd={periodEnd}
        />
      )}
    </Box>
  );
}

function EditDayDialog({
  driverId, driverName, date,
  onClose, periodStart, periodEnd,
}: {
  driverId: string;
  driverName: string;
  date: string;
  onClose: () => void;
  periodStart: string;
  periodEnd: string;
}) {
  const queryClient = useQueryClient();
  const [records, setRecords] = useState<any[] | null>(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const all = await api.getWorkHours(date, date);
      const forThisDriver = (all.entries || []).filter((r: any) => r.driverName === driverName);
      setRecords(forThisDriver);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [date, driverName]);

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: ['payroll-breakdown', driverId, periodStart, periodEnd] });
    queryClient.invalidateQueries({ queryKey: ['payroll-breakdown-all'] });
    queryClient.invalidateQueries({ queryKey: ['work-hours'] });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Registreringer for {driverName} - {date}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Alle registreringer for dagen vises. Rediger hver enkelt eller slet de skæve. Husk at vælge ny dato på slut hvis vagten går over midnat.
          </Typography>
          {loading ? <CircularProgress size={24} /> : (
            <>
              {(!records || records.length === 0) ? (
                <Alert severity="info">Ingen registreringer for dagen.</Alert>
              ) : records.map((r) => (
                <RecordRow
                  key={r.id}
                  record={r}
                  onChanged={() => { reload(); invalidateAll(); }}
                />
              ))}
              <ManualEntryRow
                driverId={driverId}
                driverName={driverName}
                date={date}
                onCreated={() => { reload(); invalidateAll(); }}
              />
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Luk</Button>
      </DialogActions>
    </Dialog>
  );
}

function RecordRow({ record, onChanged }: { record: any; onChanged: () => void }) {
  const toLocal = (iso: string | null): { date: string; time: string } => {
    if (!iso) return { date: '', time: '' };
    const d = new Date(iso);
    const pad = (n: number) => n.toString().padStart(2, '0');
    return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`, time: `${pad(d.getHours())}:${pad(d.getMinutes())}` };
  };
  const initStart = toLocal(record.started?.dateTime ?? null);
  const initEnd = toLocal(record.stopped?.dateTime ?? null);
  const [startDate, setStartDate] = useState(initStart.date);
  const [startTime, setStartTime] = useState(initStart.time);
  const [endDate, setEndDate] = useState(initEnd.date);
  const [endTime, setEndTime] = useState(initEnd.time);
  const [pauseMin, setPauseMin] = useState(String(record.pauseMinutes || 0));
  const [busy, setBusy] = useState(false);

  const startIso = startTime ? new Date(`${startDate}T${startTime}`).toISOString() : null;
  const endIso = endTime ? new Date(`${endDate}T${endTime}`).toISOString() : null;
  const diffMin = startIso && endIso ? Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 60000) : null;
  const pauseM = parseInt(pauseMin, 10) || 0;
  const workMin = diffMin != null ? diffMin - pauseM : null;
  const isError = diffMin != null && diffMin < 0;
  const crossesMidnight = startDate && endDate && startDate !== endDate;
  const srcLabel = record.source === 'WORKDAY' ? 'Rental-app' : record.source === 'DORA_TMS' ? 'DORA TMS' : 'Manuel';
  const srcColor = record.source === 'WORKDAY' ? 'primary' : 'default';

  async function handleSave() {
    setBusy(true);
    try {
      await api.updateTrip(record.routeId, {
        driverId: record.driverId,
        pauseMinutes: pauseM,
        ...(startIso ? { start: { dateTime: startIso, odometer: record.started?.odometer || '0', note: record.started?.note ?? null } } : {}),
        ...(endIso ? { end: { dateTime: endIso, odometer: record.stopped?.odometer || '0', note: record.stopped?.note ?? null } } : {}),
      });
      onChanged();
    } finally { setBusy(false); }
  }
  async function handleDelete() {
    if (!confirm(`Slet/deaktiver denne registrering (${srcLabel})?`)) return;
    setBusy(true);
    try {
      await api.deleteWorkHourEntry(record.id);
      onChanged();
    } finally { setBusy(false); }
  }

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Chip size="small" label={srcLabel} color={srcColor as any} variant="outlined" />
        {record.manuallyEdited && <Chip size="small" label="Redigeret" color="warning" variant="outlined" />}
        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontFamily: 'monospace' }}>
          {record.routeId.slice(0, 20)}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField label="Start dato" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
        <TextField label="Start tid" type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
          InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
        <TextField label="Slut dato" type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
        <TextField label="Slut tid" type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
          InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
        <TextField label="Pause min" type="number" value={pauseMin} onChange={e => setPauseMin(e.target.value)}
          size="small" inputProps={{ min: 0 }} sx={{ width: 100 }} />
      </Box>
      {diffMin != null && (
        <Typography variant="caption" color={isError ? 'error' : 'text.secondary'}>
          {isError ? 'Slut før start — kontrollér datoer' :
            `Arbejdstid: ${Math.floor(workMin! / 60)}t ${workMin! % 60}min${pauseM > 0 ? ` (efter ${pauseM}min pause)` : ''}${crossesMidnight ? ' · går over midnat' : ''}`}
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={handleSave} disabled={busy || isError}>Gem</Button>
        <Button size="small" color="error" onClick={handleDelete} disabled={busy}>Slet/deaktivér</Button>
      </Box>
    </Box>
  );
}

function ManualEntryRow({ driverId, driverName, date, onCreated }: { driverId: string; driverName: string; date: string; onCreated: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [startDate, setStartDate] = useState(date);
  const [startTime, setStartTime] = useState('');
  const [endDate, setEndDate] = useState(date);
  const [endTime, setEndTime] = useState('');
  const [pauseMin, setPauseMin] = useState('0');
  const [busy, setBusy] = useState(false);

  async function handleCreate() {
    setBusy(true);
    try {
      const startIso = startTime ? new Date(`${startDate}T${startTime}`).toISOString() : null;
      const endIso = endTime ? new Date(`${endDate}T${endTime}`).toISOString() : null;
      await api.createWorkHourEntry({
        driverId, driverName, date,
        start: startIso ? { dateTime: startIso } : undefined,
        end: endIso ? { dateTime: endIso } : undefined,
        pauseMinutes: parseInt(pauseMin, 10) || 0,
      });
      setExpanded(false);
      setStartTime(''); setEndTime(''); setPauseMin('0');
      onCreated();
    } finally { setBusy(false); }
  }

  if (!expanded) {
    return (
      <Button size="small" startIcon={<AddIcon />} onClick={() => setExpanded(true)} sx={{ alignSelf: 'flex-start' }}>
        Tilføj manuel registrering
      </Button>
    );
  }
  return (
    <Box sx={{ border: 1, borderColor: 'primary.light', borderRadius: 1, p: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Typography variant="caption" fontWeight={600}>Ny manuel registrering</Typography>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField label="Start dato" type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
        <TextField label="Start tid" type="time" value={startTime} onChange={e => setStartTime(e.target.value)}
          InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
        <TextField label="Slut dato" type="date" value={endDate} onChange={e => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
        <TextField label="Slut tid" type="time" value={endTime} onChange={e => setEndTime(e.target.value)}
          InputLabelProps={{ shrink: true }} size="small" sx={{ flex: 1 }} />
        <TextField label="Pause min" type="number" value={pauseMin} onChange={e => setPauseMin(e.target.value)}
          size="small" inputProps={{ min: 0 }} sx={{ width: 100 }} />
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained" onClick={handleCreate} disabled={busy || !startTime || !endTime}>Opret</Button>
        <Button size="small" onClick={() => setExpanded(false)}>Annuller</Button>
      </Box>
    </Box>
  );
}

function TotalRow({ label, hours, amount, suffix, hideAmount }: { label: string; hours: number | null; amount: number; suffix?: string; hideAmount?: boolean }) {
  return (
    <TableRow>
      <TableCell sx={{ pl: 0 }}>{label}{suffix}</TableCell>
      <TableCell align="right" sx={{ color: 'text.secondary' }}>
        {hours === null ? '—' : `${fmtHours(hours)} t`}
      </TableCell>
      <TableCell align="right">{hideAmount ? '' : fmt(amount)}</TableCell>
    </TableRow>
  );
}

function ManualEntryDialog({
  driverId, driverName, periodStart, periodEnd, onClose,
}: {
  driverId: string;
  driverName: string;
  periodStart: string;
  periodEnd: string;
  onClose: () => void;
}) {
  const [date, setDate] = useState(periodStart);
  const [type, setType] = useState<PayrollManualEntryType>('SYGDOM');
  const [hours, setHours] = useState('7.4');
  const [note, setNote] = useState('');
  const createMutation = useCreatePayrollManualEntry();

  async function handleSave() {
    await createMutation.mutateAsync({
      driverId,
      driverName,
      date,
      type,
      hours: parseFloat(hours),
      note: note || null,
    });
    onClose();
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Tilføj fravær — {driverName}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          <TextField
            label="Dato"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
            inputProps={{ min: periodStart, max: periodEnd }}
            InputLabelProps={{ shrink: true }}
          />
          <FormControl>
            <InputLabel>Type</InputLabel>
            <Select value={type} label="Type" onChange={e => setType(e.target.value as PayrollManualEntryType)}>
              {Object.entries(manualTypeLabels).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Timer"
            type="number"
            value={hours}
            onChange={e => setHours(e.target.value)}
            helperText="Typisk 7,4 timer = fuld arbejdsdag"
          />
          <TextField
            label="Note (valgfri)"
            multiline
            rows={2}
            value={note}
            onChange={e => setNote(e.target.value)}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuller</Button>
        <Button variant="contained" onClick={handleSave} disabled={createMutation.isPending || !hours}>
          {createMutation.isPending ? <CircularProgress size={16} /> : 'Gem'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}


// =================================================================
// Eksport-dialog — vælger format. Fra-træk-typer er faste (BREAK + SICK).
// =================================================================

interface ExportPayrollCsvDialogProps {
  from: string;
  to: string;
  selectedDrivers: Array<{ driverId: string; driverName: string }>;
  totalDrivers: number;
  onClose: () => void;
}

function ExportPayrollCsvDialog({ from, to, selectedDrivers, totalDrivers, onClose }: ExportPayrollCsvDialogProps) {
  // Standard fra-træk: pauser (BREAK) trukket fra automatisk via pauseMinutes
  // og sygdoms-aktiviteter (SICK) skal heller ikke tælles som arbejdstid.
  // Brugeren ønsker ikke at vælge hver gang — disse er faste defaults.
  const DEFAULT_SUBTRACT = 'BREAK,SICK';
  const [downloading, setDownloading] = useState<null | 'danlon' | 'datalon-branche' | 'datalon' | 'pdf'>(null);
  // Serveren fortæller via response-headers hvem der IKKE kom med i filen. Et
  // lønudtræk der lydløst udelader medarbejdere ser rigtigt ud, og de manglende
  // timer opdages først når nogen ikke får løn — så det skal frem på skærmen.
  const [skipWarning, setSkipWarning] = useState<{ count: number; names: string; ambiguous: string; zeroRateCount: number; zeroRateLines: string } | null>(null);
  // Serveren afviser fx et Dataløn-udtræk hvis virksomhedsnr eller løntermin
  // mangler, og skriver præcis hvad der mangler. Uden det her blev fejlen
  // kastet ud i ingenting, og brugeren så bare at der ikke skete noget.
  const [downloadError, setDownloadError] = useState<string | null>(null);
  // Løntermin er Dataløns faste terminkode, fx 35 for 14-dages løn. Den hentes
  // fra opsætningen og kan rettes her, hvis kørslen er en anden lønform.
  const [loentermin, setLoentermin] = useState('');
  const [loenterminLabel, setLoenterminLabel] = useState('');

  useEffect(() => {
    let afbrudt = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch('/api/export/datalon-loentermin', {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok || afbrudt) return;
        const body = await res.json();
        if (afbrudt) return;
        setLoentermin(body?.data?.value || '');
        setLoenterminLabel(body?.data?.label || '');
      } catch { /* feltet kan udfyldes i hånden */ }
    })();
    return () => { afbrudt = true; };
  }, [from, to]);

  const downloadParams = {
    from, to,
    driverIds: selectedDrivers.map(d => d.driverId).join(','),
    subtractKinds: DEFAULT_SUBTRACT,
  };

  const download = async (kind: 'danlon' | 'datalon-branche' | 'datalon' | 'pdf') => {
    setDownloading(kind);
    setDownloadError(null);
    try {
      const url = kind === 'danlon'
        ? `/api/export/csv/danlon-work-hours`
        : kind === 'datalon-branche'
        ? `/api/export/csv/datalon-branche-work-hours`
        : kind === 'datalon'
        ? `/api/export/csv/datalon-work-hours`
        : `/api/payroll/breakdown/pdf`;
      const filename = kind === 'danlon'
        ? `danlon_timer_${from}_${to}.csv`
        : kind === 'datalon-branche'
        ? `datalon_branche_${from}_${to}.csv`
        : kind === 'datalon'
        ? `datalon_${from}_${to}.csv`
        : `lonoverblik_${from}_${to}.pdf`;
      const params = new URLSearchParams({
        from: downloadParams.from, to: downloadParams.to,
        ...(downloadParams.driverIds ? { driverIds: downloadParams.driverIds } : {}),
        ...(downloadParams.subtractKinds ? { subtractKinds: downloadParams.subtractKinds } : {}),
        ...(kind === 'datalon' && loentermin.trim() ? { loentermin: loentermin.trim() } : {}),
      });
      const token = localStorage.getItem('token');
      const res = await fetch(`${url}?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        // Fejlsvaret er JSON, ikke en fil. Læs serverens begrundelse frem for
        // at vise en statuskode brugeren ikke kan gøre noget ved.
        let besked = `Udtrækket fejlede (HTTP ${res.status})`;
        try {
          const body = await res.json();
          if (body?.error) besked = String(body.error);
        } catch { /* ikke JSON, behold standardteksten */ }
        throw new Error(besked);
      }
      const skippedCount = Number(res.headers.get('X-Export-Skipped-Count') || 0);
      const skippedNames = decodeURIComponent(res.headers.get('X-Export-Skipped-Drivers') || '');
      const ambiguous = decodeURIComponent(res.headers.get('X-Export-Ambiguous-Names') || '');
      const zeroRateCount = Number(res.headers.get('X-Export-Zero-Rate-Count') || 0);
      const zeroRateLines = decodeURIComponent(res.headers.get('X-Export-Zero-Rate-Lines') || '');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 10000);
      // Filen er hentet uanset hvad. Blev nogen udeladt, holder vi dialogen
      // åben med en advarsel i stedet for at lukke som om alt gik godt.
      if (skippedCount > 0 || ambiguous || zeroRateCount > 0) {
        setSkipWarning({ count: skippedCount, names: skippedNames, ambiguous, zeroRateCount, zeroRateLines });
      } else {
        onClose();
      }
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Udtrækket fejlede');
    } finally {
      setDownloading(null);
    }
  };

  const driverFilterActive = selectedDrivers.length !== totalDrivers;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Eksport løn</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Alert severity={driverFilterActive ? 'info' : 'success'}>
            {driverFilterActive
              ? `${selectedDrivers.length} af ${totalDrivers} chauffør(er) inkluderes`
              : `Alle ${totalDrivers} chauffører inkluderes`}
          </Alert>
          {downloadError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setDownloadError(null)}>
              {downloadError}
            </Alert>
          )}
          {skipWarning && skipWarning.zeroRateCount > 0 && (
            <Alert severity="warning">
              <strong>Filen er hentet, men {skipWarning.zeroRateCount} linje(r) fik satsen 0,00 og dermed beløbet 0.</strong>{' '}
              Dataløn indlæser linjerne, men uden penge på dem. Mangler satsen under
              Indstillinger → Dataløn, eller er den tastet på en anden chauffør-profil
              end den timerne er registreret på? Berørt: {skipWarning.zeroRateLines}.
            </Alert>
          )}
          {skipWarning && skipWarning.count > 0 && (
            <Alert severity="warning">
              <strong>{skipWarning.count} chauffør(er) kom ikke med i filen.</strong>
              {skipWarning.names && (
                <> Uden medarbejdernummer: {skipWarning.names}. Opret mappingen under
                Indstillinger → Chauffører, og hent filen igen.</>
              )}
            </Alert>
          )}
          {skipWarning && skipWarning.ambiguous && (
            <Alert severity="warning">
              Flere chauffører deler navn med forskellige medarbejdernumre
              ({skipWarning.ambiguous}) — dem tør vi ikke gætte på.
            </Alert>
          )}
          <Typography variant="caption" color="text.secondary">
            Bruger de lønarter du har opsat under Indstillinger → Dataløn Branche.
            Pauser og sygdoms-aktiviteter trækkes automatisk fra de fakturerbare timer.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button variant="outlined"
              startIcon={downloading === 'pdf' ? <CircularProgress size={14} /> : <DownloadIcon />}
              disabled={!!downloading} onClick={() => download('pdf')}>
              Kompakt PDF (fact-check)
            </Button>
            <Button variant="outlined"
              startIcon={downloading === 'danlon' ? <CircularProgress size={14} /> : <DownloadIcon />}
              disabled={!!downloading} onClick={() => download('danlon')}>
              Danløn CSV
            </Button>
            <Button variant="outlined"
              startIcon={downloading === 'datalon-branche' ? <CircularProgress size={14} /> : <DownloadIcon />}
              disabled={!!downloading} onClick={() => download('datalon-branche')}>
              Dataløn Branche CSV
            </Button>
            <Stack direction="row" spacing={1} alignItems="center">
              <TextField
                label="Løntermin"
                size="small"
                value={loentermin}
                onChange={e => setLoentermin(e.target.value)}
                helperText={loenterminLabel}
                sx={{ width: 130 }}
              />
              <Button variant="outlined"
                startIcon={downloading === 'datalon' ? <CircularProgress size={14} /> : <DownloadIcon />}
                disabled={!!downloading} onClick={() => download('datalon')}>
                Dataløn CSV
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Luk</Button>
      </DialogActions>
    </Dialog>
  );
}

// =================================================================
// DriverFilterButton — popover med søgning + checkbox-liste
// =================================================================
interface DriverFilterButtonProps {
  breakdowns: PayrollBreakdown[];
  filterDriverIds: string[];
  onChange: (ids: string[]) => void;
}

function DriverFilterButton({ breakdowns, filterDriverIds, onChange }: DriverFilterButtonProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [search, setSearch] = useState('');

  const sorted = useMemo(
    () => [...breakdowns].sort((a, b) => a.driverName.localeCompare(b.driverName, 'da')),
    [breakdowns],
  );
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? sorted.filter(b => b.driverName.toLowerCase().includes(q)) : sorted;
  }, [sorted, search]);

  const selectedSet = useMemo(() => new Set(filterDriverIds), [filterDriverIds]);
  const allSelected = filterDriverIds.length === 0; // "ingen filter" = alle vises
  const filteredAllChecked = filtered.length > 0 && filtered.every(b => selectedSet.has(b.driverId));

  const toggle = (id: string) => {
    if (allSelected) {
      // Fra "alle vises" → vis alle UNDTAGEN den der fravælges
      onChange(sorted.map(b => b.driverId).filter(x => x !== id));
      return;
    }
    if (selectedSet.has(id)) {
      const next = filterDriverIds.filter(x => x !== id);
      // Hvis brugeren fravælger ALLE → tilbage til "vis alle" (tom liste betyder alle)
      if (next.length === 0) onChange([]);
      else onChange(next);
    } else {
      const next = [...filterDriverIds, id];
      // Hvis alle nu er valgt → reducer til tom (= vis alle)
      if (next.length === sorted.length) onChange([]);
      else onChange(next);
    }
  };

  const selectAll = () => onChange([]);
  const selectAllFiltered = () => {
    const idsToAdd = filtered.map(b => b.driverId).filter(id => !selectedSet.has(id));
    onChange([...filterDriverIds, ...idsToAdd]);
  };
  const clearAllFiltered = () => {
    const filteredIds = new Set(filtered.map(b => b.driverId));
    onChange(filterDriverIds.filter(id => !filteredIds.has(id)));
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <Badge
        color="primary"
        badgeContent={filterDriverIds.length > 0 ? filterDriverIds.length : null}
        overlap="rectangular"
      >
        <Button
          variant="outlined"
          size="small"
          startIcon={<FilterListIcon />}
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          {filterDriverIds.length === 0
            ? `Alle chauffører (${sorted.length})`
            : `${filterDriverIds.length} chauffør${filterDriverIds.length === 1 ? '' : 'er'}`}
        </Button>
      </Badge>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { width: 340, maxHeight: 480, display: 'flex', flexDirection: 'column' } } }}
      >
        <Box sx={{ p: 1.5, pb: 1 }}>
          <TextField
            size="small"
            fullWidth
            autoFocus
            placeholder="Søg chauffør…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch('')}><ClearIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Stack direction="row" spacing={0.5} sx={{ mt: 1 }}>
            <Button size="small" onClick={selectAll} disabled={allSelected}>Vis alle</Button>
            {search && (
              <>
                <Button size="small" onClick={selectAllFiltered} disabled={filteredAllChecked}>Vælg filtrerede</Button>
                <Button size="small" onClick={clearAllFiltered}>Fjern filtrerede</Button>
              </>
            )}
          </Stack>
        </Box>
        <Divider />
        <Box sx={{ overflowY: 'auto', flex: 1 }}>
          {filtered.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ p: 2, textAlign: 'center' }}>
              Ingen chauffører matcher
            </Typography>
          ) : (
            <List dense disablePadding>
              {filtered.map(b => {
                const checked = allSelected || selectedSet.has(b.driverId);
                return (
                  <ListItemButton key={b.driverId} onClick={() => toggle(b.driverId)} sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Checkbox edge="start" size="small" checked={checked} tabIndex={-1} />
                    </ListItemIcon>
                    <ListItemText primary={<Typography variant="body2">{b.driverName}</Typography>} />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Popover>
    </>
  );
}


// Send loenoverblik til disponent til godkendelse. Vaelg gruppe ELLER
// vaelg chauffoerer enkeltvis. Server genererer 1 PDF pr chauffoer +
// 1 EML-fil med alle PDFs vedhaeftet og approval-links pr chauffoer.
function SendForApprovalDialog({ from, to, availableDrivers, onClose }: {
  from: string; to: string;
  availableDrivers: Array<{ driverId: string; driverName: string }>;
  onClose: () => void;
}) {
  const [groups, setGroups] = useState<Array<{ id: string; name: string; approverEmail: string; approverName: string | null; driverIds: string[] }>>([]);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [approverEmail, setApproverEmail] = useState('');
  const [approverName, setApproverName] = useState('');
  const [selectedDriverIds, setSelectedDriverIds] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.getApproverGroups().then(g => { setGroups(g); setGroupsLoaded(true); });
  }, []);

  function applyGroup(groupId: string) {
    setSelectedGroupId(groupId);
    const g = groups.find(x => x.id === groupId);
    if (!g) return;
    setApproverEmail(g.approverEmail);
    setApproverName(g.approverName || '');
    const available = new Set(availableDrivers.map(d => d.driverId));
    setSelectedDriverIds(g.driverIds.filter(id => available.has(id)));
  }

  async function handleSend() {
    setErr(null);
    if (!approverEmail.trim()) { setErr('Indtast disponent-email'); return; }
    if (selectedDriverIds.length === 0) { setErr('Vælg mindst én chauffør'); return; }
    setCreating(true);
    try {
      const batch = await api.createPayrollApprovalBatch({
        periodStart: from, periodEnd: to,
        approverEmail: approverEmail.trim(),
        approverName: approverName.trim() || null,
        groupId: selectedGroupId || null,
        driverIds: selectedDriverIds,
      });
      await api.downloadPayrollApprovalEml(batch.batchId);
      onClose();
    } catch (e: any) {
      setErr(e?.response?.data?.error || 'Kunne ikke sende');
    } finally {
      setCreating(false);
    }
  }

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Send lønoverblik til godkendelse</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Genererer 1 PDF pr chauffør med godkend/afvis-links og samler dem i én mail.
            Mailen åbner i din egen klient så den ikke ender i spam.
          </Typography>

          {groupsLoaded && groups.length > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>Gruppe</InputLabel>
              <Select
                value={selectedGroupId}
                label="Gruppe"
                onChange={e => applyGroup(e.target.value as string)}
              >
                <MenuItem value=""><em>Manuelt valg</em></MenuItem>
                {groups.map(g => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.name} ({g.approverEmail}, {g.driverIds.length} chauffører)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField size="small" label="Disponent-navn (valgfri)" value={approverName} onChange={e => setApproverName(e.target.value)} sx={{ flex: 1 }} />
            <TextField size="small" label="Disponent-email" type="email" value={approverEmail} onChange={e => setApproverEmail(e.target.value)} sx={{ flex: 2 }} required />
          </Box>

          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Chauffører ({selectedDriverIds.length}/{availableDrivers.length})
            </Typography>
            <Box sx={{ maxHeight: 280, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
              {availableDrivers.map(d => (
                <ListItemButton
                  key={d.driverId}
                  dense
                  onClick={() => setSelectedDriverIds(prev =>
                    prev.includes(d.driverId) ? prev.filter(x => x !== d.driverId) : [...prev, d.driverId]
                  )}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox edge="start" size="small" checked={selectedDriverIds.includes(d.driverId)} />
                  </ListItemIcon>
                  <ListItemText primary={d.driverName} />
                </ListItemButton>
              ))}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
              <Button size="small" onClick={() => setSelectedDriverIds(availableDrivers.map(d => d.driverId))}>Vælg alle</Button>
              <Button size="small" onClick={() => setSelectedDriverIds([])}>Fravælg alle</Button>
            </Box>
          </Box>

          {err && <Alert severity="error">{err}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuller</Button>
        <Button variant="contained" onClick={handleSend} disabled={creating}>
          {creating ? <CircularProgress size={20} /> : 'Generér og åbn i min mail'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
